import { AddIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  Select,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useReducedMotion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ColumnManagement from '../../../components/column-management/ColumnManagement';
import FilterDrawer from '../../../components/filter-drawer/FilterDrawer';
import FilterStateIndicator from '../../../components/filter-state-indicator/FilterStateIndicator';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import Pagination from '../../../components/pagination/Pagination';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import ViewToggle from '../../../components/view-toggle/ViewToggle';
import { useColumnManagement } from '../../../hooks/useColumnManagement';
import { useCustomFields } from '../../../hooks/useCustomFields';
import { usePagination } from '../../../hooks/usePagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { getPracticesList } from '../../../services/practices/getPractices';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import {
  OfficeHour,
  Practice,
  PracticesResponse,
} from '../../../types/practices/types';
import { analytics } from '../../../utils/analytics';
import { getClientIdWithFallback } from '../../../utils/customFieldUtils';
import {
  convertToColumns,
  getAllPracticeColumnsWithRenders,
} from './PracticeColumns';
import { filterOptions } from './practiceFilterOptions';
import { practiceFilterMappings } from './practiceFilterMappings';
import { PracticeItem } from './PracticeItem';
import { useBatchedClosures } from '../../../hooks/useBatchedClosures';
import { useBroadSpecialtyOptions } from '../../../hooks/useBroadSpecialtyOptions';
import { extractSpecialtyOptions } from '../../../utils/broadSpecialtyOptions';
import {
  applyExternalFilterForNonAdmin,
  isExternalMarket,
} from '../../../utils/externalEntity';
import {
  listItemVariants,
  workflowStaggerContainer,
} from '../../../utils/motion';
import { MotionBox } from '../../../components/motion/MotionComponents';
import { stateOptions } from '../../../utils/stateOptions';

interface GroupedOfficeHours {
  [practice_id: string]: OfficeHour[];
}

const FILTERS_STORAGE_KEY = 'practicesFilters';

const PracticeList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const prefersReducedMotion = useReducedMotion();
  const { canEditPractices, isAdmin, permissions, isReady } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get client ID for custom fields
  const clientId = getClientIdWithFallback();

  // Fetch custom fields for Practice table
  const { customFields: practiceCustomFields } = useCustomFields(
    clientId,
    'Practice'
  );

  const {
    error: practiceBroadSpecialtyError,
  } = useBroadSpecialtyOptions({ entityType: 'Practice', clientId });

  // Extract Specialty options from custom fields for Practices
  const practiceSpecialtyOptions = useMemo(
    () => extractSpecialtyOptions(practiceCustomFields),
    [practiceCustomFields]
  );

  // Generate dynamic columns based on custom fields
  const dynamicColumns = useMemo(() => {
    return getAllPracticeColumnsWithRenders(
      practiceCustomFields || []
    );
  }, [practiceCustomFields]);

  // Column management
  const {
    visibleColumns,
    hiddenColumns,
    toggleColumnVisibility,
    reorderColumns,
    resetColumns,
  } = useColumnManagement<Practice>({
    defaultColumns: dynamicColumns,
    storageKey: 'practice-table-columns',
  });

  const buttonBg = useColorModeValue(
    'background.secondary',
    'gray.700'
  );
  const buttonHoverBg = useColorModeValue('gray.300', 'gray.600');
  const selectBorderColor = 'background.secondary';

  // State
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');

  // Load filters from localStorage on mount
  const [filters, setFilters] = useState<
    Record<string, string | string[]>
  >(() => {
    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [sortOrder, setSortOrder] = useState('ascending');
  const [officeHours, setOfficeHours] = useState<GroupedOfficeHours>(
    {}
  );
  const [clearFiltersSignal, setClearFiltersSignal] = useState(false);
  const [officeHoursLoading, setOfficeHoursLoading] = useState(false);
  const fetchedOfficeHoursRef = useRef<Set<string>>(new Set());
  const filtersRef = useRef<Record<string, string | string[]>>({});
  const lastFetchedFilters = useRef<string>(''); // Store last successful filters
  const lastFetchedResult = useRef<number>(-1); // Track last fetched result count
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('q') || ''
  );

  // Fetch practices
  const {
    items: practices,
    loading,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    setItems,
    refetch,
  } = usePagination<Practice, PracticesResponse>(
    useCallback(
      (startToken) =>
        getPracticesList(
          startToken,
          applyExternalFilterForNonAdmin(filters, permissions.isAdmin),
          10
        ),
      [filters, permissions.isAdmin]
    ),
    10,
    {},
    undefined,
    isReady
  );

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(filters)
      );
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [filters]);

  const clearFilters = () => {
    setFilters({}); // Clear all filters
    filtersRef.current = {};
    lastFetchedFilters.current = ''; // Reset last fetched filters
    lastFetchedResult.current = -1; // Reset last fetched result count
    setSearchQuery('');
    setSearchParams({});
    setClearFiltersSignal((prev) => !prev);
    // Clear from localStorage
    localStorage.removeItem(FILTERS_STORAGE_KEY);
    refetch?.(); // Fetch without filters
  };

  // Handle search query changes with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        setSearchParams({ q: searchQuery.trim() });
        setFilters((prev) => ({ ...prev, q: searchQuery.trim() }));
        analytics.trackEvent('search', 'practice', {
          queryLength: searchQuery.trim().length,
        });
      } else {
        setSearchParams({});
        setFilters((prev) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { q, ...rest } = prev;
          return rest;
        });
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, setSearchParams]);

  // Initialize search query from URL on mount only
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && !searchQuery) {
      setSearchQuery(urlQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Intentionally omitting searchQuery to prevent input conflicts

  // Get market options from localStorage
  const statesData = useMemo(
    () => JSON.parse(localStorage.getItem('statesData') || '[]'),
    []
  );

  const visibleStatesData = useMemo(() => {
    if (permissions.isAdmin) {
      return statesData;
    }

    return statesData
      .map(
        (state: {
          markets: { id: string; name: string; external?: boolean }[];
        }) => ({
          ...state,
          markets: (state.markets || []).filter(
            (market) => !isExternalMarket(market)
          ),
        })
      )
      .filter(
        (state: { markets: { id: string; name: string }[] }) =>
          state.markets.length > 0
      );
  }, [statesData, permissions.isAdmin]);

  // Memoize filter options to prevent recreation on every render
  // This ensures FilterDrawer receives updated options when specialtyOptions loads
  const memoizedFilterOptions = useMemo(
    () =>
      filterOptions(
        visibleStatesData,
        practiceSpecialtyOptions,
        practiceCustomFields
      ),
    [
      visibleStatesData,
      practiceSpecialtyOptions,
      practiceCustomFields,
    ]
  );

  useEffect(() => {
    if (practiceBroadSpecialtyError) {
      toast({
        title: 'Specialty filters limited',
        description:
          'We could not load Broad-specialty options. Retry in a bit or refresh the page.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [practiceBroadSpecialtyError, toast]);

  // Sorting logic
  const sortPractices = useCallback(() => {
    setItems((currentItems) =>
      [...currentItems].sort((a, b) => {
        const nameA = a.name?.toUpperCase() ?? '';
        const nameB = b.name?.toUpperCase() ?? '';
        return sortOrder === 'ascending'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      })
    );
  }, [sortOrder, setItems]);

  // Create a new practices array with office hours merged
  const practicesWithOfficeHours = useMemo(() => {
    return practices.map((practice) => ({
      ...practice,
      office_hours: officeHours[practice.id] || [],
    }));
  }, [practices, officeHours]);

  // Batch-fetch closures for all visible practices in a single call
  const practiceIds = useMemo(() => practices.map((p) => p.id), [practices]);
  const { closuresByPractice, isLoading: closuresLoading } = useBatchedClosures(practiceIds);

  // Fetch office hours for practices
  const fetchOfficeHours = useCallback(
    async (practiceIds: string[]) => {
      if (practiceIds.length === 0) return;

      const unfetchedIds = practiceIds.filter(
        (id) => !fetchedOfficeHoursRef.current.has(id)
      );

      if (unfetchedIds.length === 0) return;

      setOfficeHoursLoading(true);
      try {
        const response = await postOfficeHoursList(practiceIds);
        if (response.data && response.data.office_hours) {
          const groupedHours: GroupedOfficeHours = {};
          response.data.office_hours.forEach(
            (officeHour: OfficeHour) => {
              if (officeHour.practice_id) {
                if (!groupedHours[officeHour.practice_id]) {
                  groupedHours[officeHour.practice_id] = [];
                }
                groupedHours[officeHour.practice_id].push(officeHour);
              }
            }
          );

          setOfficeHours((prev) => ({
            ...prev,
            ...groupedHours,
          }));

          // Mark these practice IDs as fetched
          unfetchedIds.forEach((id) =>
            fetchedOfficeHoursRef.current.add(id)
          );
        }
      } catch (error) {
        console.error('Failed to fetch office hours:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch office hours',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setOfficeHoursLoading(false);
      }
    },
    [toast]
  );

  // Fetch office hours when practices change
  useEffect(() => {
    if (practices.length > 0) {
      const practiceIds = practices.map((practice) => practice.id);
      fetchOfficeHours(practiceIds);
    }
  }, [practices, fetchOfficeHours]);

  // Sort practices when sort order changes
  useEffect(() => {
    sortPractices();
  }, [sortPractices]);

  // Debounced refetch to prevent excessive API calls
  useEffect(() => {
    const currentFilters = JSON.stringify(filters);

    // Skip on initial mount — usePagination already fetches on mount
    if (lastFetchedFilters.current === '') {
      lastFetchedFilters.current = currentFilters;
      return;
    }

    if (currentFilters === lastFetchedFilters.current) return; // Prevent duplicate calls

    const timer = setTimeout(() => {
      if (refetch) {
        refetch();
        lastFetchedFilters.current = currentFilters;
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filters, refetch]);

  const handleApplyFilters = useCallback((
    newFilters: Record<string, string[] | string>
  ) => {
    try {
      // Start fresh - only include filters that are in newFilters
      // This ensures removed filters are properly cleared
      const formattedFilters: Record<string, string | string[]> = {};

      // Process all filters from newFilters (only selected filters are included)
      Object.entries(newFilters).forEach(([uiKey, value]) => {
        if (value && value !== '' && (Array.isArray(value) ? value.length > 0 : true)) {
          const apiKey = practiceFilterMappings[uiKey] || uiKey;
          formattedFilters[apiKey] = value;
        }
        // If value is empty, don't add it (filter was removed)
      });

      setFilters(formattedFilters);
      filtersRef.current = newFilters;

      // Reset to first page when applying new filters
      if (handlePageChange) {
        handlePageChange(1);
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      // Reset filters on error
      setFilters({});
      filtersRef.current = {};
    }
  }, [handlePageChange]);

  // Get market options for FilterStateIndicator
  const marketOptions = useMemo(() => {
    return visibleStatesData
      .flatMap((state: { markets: { id: string; name: string }[] }) =>
        state.markets.map((market) => ({
          label: market.name,
          value: market.id,
        }))
      )
      .sort(
        (
          a: { label: string; value: string },
          b: { label: string; value: string }
        ) => a.label.localeCompare(b.label)
      );
  }, [visibleStatesData]);

  return (
    <>
      <Helmet>
        <title>Practices | CHS | Resource Matrix — Denim Health</title>
      </Helmet>
      <MotionBox
        ml="11.25rem"
        mr="11.25rem"
        mb="3rem"
        mt="1.75rem"
        initial={
          prefersReducedMotion
            ? { opacity: 1, y: 0 }
            : { opacity: 0, y: 6 }
        }
        animate={{ opacity: 1, y: 0 }}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mb="1rem"
        >
          <Text
            fontSize="1.25rem"
            fontWeight="700"
            color="text.secondary"
          >
            Practices
          </Text>
          <Flex gap="0.75rem" alignItems="center">
            {(canEditPractices() || isAdmin()) && (
              <Button
                colorScheme="blue"
                background="background.brandLight"
                leftIcon={<AddIcon />}
                size="md"
                px={6}
                py={3}
                mr="0.75rem"
                onClick={() => navigate('/practices/create')}
              >
                Add Resource
              </Button>
            )}
            <Select
              bg={buttonBg}
              width="10.375rem"
              mr="0.5rem"
              height="2.5rem"
              _hover={{ bg: buttonHoverBg }}
              _active={{
                bg: buttonHoverBg,
                transform: 'scale(0.98)',
              }}
              borderColor={selectBorderColor}
              _focus={{ boxShadow: 'outline' }}
              borderRadius={'0.375rem'}
              border="none"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="ascending">Name A - Z</option>
              <option value="descending">Name Z - A</option>
            </Select>
            <ViewToggle
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
            {viewMode === 'table' && (
              <ColumnManagement
                columns={[...visibleColumns, ...hiddenColumns]}
                visibleColumns={visibleColumns}
                hiddenColumns={hiddenColumns}
                onToggleVisibility={toggleColumnVisibility}
                onReorder={reorderColumns}
                onReset={resetColumns}
              />
            )}
            <FilterDrawer
              filterTitle="Practices"
              filterOptions={memoizedFilterOptions}
              onApplyFilters={handleApplyFilters}
              clearFilters={clearFiltersSignal}
              initialFilters={(() => {
                // Convert API keys back to UI keys for FilterDrawer
                const uiFilters: Record<string, string | string[]> = {};
                Object.entries(filters).forEach(([apiKey, value]) => {
                  // Find UI key from mapping (reverse lookup)
                  const uiKey =
                    Object.keys(practiceFilterMappings).find(
                      (key) => practiceFilterMappings[key] === apiKey
                    ) || apiKey;
                  uiFilters[uiKey] = value;
                });
                return uiFilters;
              })()}
            />
          </Flex>
        </Flex>

        {/* Filter State Indicator */}
        <FilterStateIndicator
          appliedFilters={filters}
          totalCount={totalCount || 0}
          filteredCount={practicesWithOfficeHours.length}
          resourceType="practices"
          onClearFilters={clearFilters}
          marketOptions={marketOptions}
          stateOptions={stateOptions}
        />

        {loading ? (
          <PageLoader height="45vh" />
        ) : practicesWithOfficeHours.length > 0 ? (
          viewMode === 'table' ? (
            <MotionBox
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <ResourceGrid<Practice>
                data={practicesWithOfficeHours}
                columns={convertToColumns(visibleColumns)}
              />
            </MotionBox>
          ) : (
            <MotionBox
              display="flex"
              flexDirection="column"
              variants={
                prefersReducedMotion
                  ? undefined
                  : workflowStaggerContainer
              }
              initial={prefersReducedMotion ? undefined : 'initial'}
              animate={prefersReducedMotion ? undefined : 'animate'}
              gap="1.5rem"
              mb="2.31rem"
            >
              {practicesWithOfficeHours.map((practice) => (
                <MotionBox
                  key={practice.id}
                  variants={
                    prefersReducedMotion
                      ? undefined
                      : listItemVariants
                  }
                >
                  <PracticeItem
                    practice={practice}
                    onClick={() =>
                      navigate(`/practices/${practice.id}`)
                    }
                    officeHoursLoading={officeHoursLoading}
                    clinicClosures={
                      closuresLoading
                        ? null
                        : closuresByPractice[practice.id]
                    }
                    isClosuresLoading={closuresLoading}
                  />
                </MotionBox>
              ))}
            </MotionBox>
          )
        ) : (
          <NoItemsFound
            mainMessage="No Practices Found"
            detailMessage="We couldn't find anything matching your search criteria."
            refetch={refetch}
            removeFilters={
              Object.keys(filters).length > 0
                ? clearFilters
                : undefined
            }
          />
        )}

        {/* Helper text showing current page range */}
        {!loading &&
          totalCount > 0 &&
          practicesWithOfficeHours.length > 0 && (
            <Text
              fontSize="0.875rem"
              color="gray.600"
              mb="1rem"
              textAlign="center"
            >
              Showing {(currentPage - 1) * 10 + 1}–
              {Math.min(currentPage * 10, totalCount)} of {totalCount}
            </Text>
          )}

        {totalPages > 1 && practicesWithOfficeHours.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </MotionBox>
    </>
  );
};

export default PracticeList;
